import React from 'react';

import { Minus } from '@saleshandy/icons';

import type { IProps } from './prospect-filters-value-component-container';
import { FilterOperation } from '../../types';

import {
  dateDropdownOperations,
  isDateField,
  multiSelectOperations,
  numberInputOperations,
  textInputOperations,
} from '../../utils/helpers';

import ProspectFiltersTextInput from './components/prospect-filters-text-input';
import ProspectFiltersNumberInput from './components/prospect-filters-number-input';
import ProspectFiltersDateInput from './components/prospect-filters-date-input';
import ProspectFiltersSelectInput from './components/prospect-filters-select-input';
import ProspectFiltersSelectDropdown from '../prospect-filters-select-dropdown';
import { getFreeEmailAccountMapping } from '../../../../../../shared/utils/get-free-email-account-mapping';

const ProspectFiltersValueComponent: React.FC<IProps> = ({
  field,
  operation,
  value,
  onUpdateFilterConditionValue,
  disabled = false,

  prospectFiltersFieldsAttributesValue,
  prospectFiltersFieldsAttributesLoading,
  prospectFiltersFieldsAttributesFetched,
  sendGetProspectFiltersFieldValuesRequest,
  sendGetProspectFiltersAttributeFieldValuesRequest,
  sendGetProspectFiltersSequencesListRequest,
  sendGetProspectFiltersClientsListRequest,
  sendGetProspectFiltersSubsequenceListRequest,
  sendGetProspectFiltersTagsListRequest,
  sendGetProspectFiltersOwnersListRequest,
  sendGetProspectFiltersOwnersListForAgencyRequest,
  sendGetProspectFiltersOutcomeListRequest,
  sendGetProspectLeadFinderListsRequest,
  resetProspectFiltersFieldsAttributesValue,
  onApplyAiColEnrichmentAutoRunSettingsCondition,
  fields,
}) => {
  if (multiSelectOperations.includes(operation)) {
    if (isDateField(field, fields)) {
      return (
        <ProspectFiltersDateInput
          field={field}
          operation={operation}
          value={value as string}
          onUpdateFilterConditionValue={onUpdateFilterConditionValue}
          disabled={disabled}
        />
      );
    }

    if (field.key === 'freeEmailAccount') {
      return (
        <ProspectFiltersSelectDropdown
          className="date-input-select-box w-100"
          selectClassName="date-input-dropdown"
          options={getFreeEmailAccountMapping()}
          placeholder="Select Yes/No"
          selectedOptionKey={(() => {
            if (!value) {
              return null;
            }

            if (Array.isArray(value) && value?.length !== 0) {
              return value?.[0]?.toString();
            }

            return value?.toString();
          })()}
          optionRenderer={(option) => <span>{option?.value}</span>}
          selectedOptionRenderer={(option) => <span>{option?.value}</span>}
          onChange={(option) => {
            onUpdateFilterConditionValue([option.key]);
          }}
        />
      );
    }

    return (
      <ProspectFiltersSelectInput
        value={value as string[]}
        operation={operation}
        onUpdateFilterConditionValue={onUpdateFilterConditionValue}
        field={field}
        disabled={disabled}
        prospectFiltersFieldsAttributesValue={
          prospectFiltersFieldsAttributesValue
        }
        prospectFiltersFieldsAttributesLoading={
          prospectFiltersFieldsAttributesLoading
        }
        prospectFiltersFieldsAttributesFetched={
          prospectFiltersFieldsAttributesFetched
        }
        sendGetProspectFiltersFieldValuesRequest={
          sendGetProspectFiltersFieldValuesRequest
        }
        sendGetProspectFiltersAttributeFieldValuesRequest={
          sendGetProspectFiltersAttributeFieldValuesRequest
        }
        sendGetProspectFiltersSequencesListRequest={
          sendGetProspectFiltersSequencesListRequest
        }
        sendGetProspectFiltersClientsListRequest={
          sendGetProspectFiltersClientsListRequest
        }
        sendGetProspectFiltersSubsequenceListRequest={
          sendGetProspectFiltersSubsequenceListRequest
        }
        sendGetProspectFiltersTagsListRequest={
          sendGetProspectFiltersTagsListRequest
        }
        sendGetProspectFiltersOwnersListRequest={
          sendGetProspectFiltersOwnersListRequest
        }
        sendGetProspectFiltersOwnersListForAgencyRequest={
          sendGetProspectFiltersOwnersListForAgencyRequest
        }
        sendGetProspectFiltersOutcomeListRequest={
          sendGetProspectFiltersOutcomeListRequest
        }
        sendGetProspectLeadFinderListsRequest={
          sendGetProspectLeadFinderListsRequest
        }
        resetProspectFiltersFieldsAttributesValue={
          resetProspectFiltersFieldsAttributesValue
        }
        onApplyAiColEnrichmentAutoRunSettingsCondition={
          onApplyAiColEnrichmentAutoRunSettingsCondition
        }
      />
    );
  }

  if (textInputOperations.includes(operation)) {
    return (
      <ProspectFiltersTextInput
        value={(value === '' ? [''] : value) as string[]}
        onUpdateFilterConditionValue={onUpdateFilterConditionValue}
        disabled={disabled}
      />
    );
  }

  if (numberInputOperations.includes(operation)) {
    if (operation === FilterOperation.BETWEEN) {
      return (
        <div className="number-input-container">
          <div className="number-input">
            <ProspectFiltersNumberInput
              placeholder="Min"
              value={(value?.[0] || '') as string}
              onUpdateFilterConditionValue={(updatedValue) => {
                onUpdateFilterConditionValue([updatedValue, value?.[1] || '']);
              }}
              disabled={disabled}
            />
          </div>
          <div className="number-input-separator">
            <Minus />
          </div>
          <div className="number-input">
            <ProspectFiltersNumberInput
              placeholder="Max"
              value={(value?.[1] || '') as string}
              onUpdateFilterConditionValue={(updatedValue) => {
                onUpdateFilterConditionValue([value?.[0] || '', updatedValue]);
              }}
              disabled={disabled}
            />
          </div>
        </div>
      );
    }

    return (
      <ProspectFiltersNumberInput
        value={value as string}
        onUpdateFilterConditionValue={(updatedValue) => {
          onUpdateFilterConditionValue(updatedValue);
        }}
        disabled={disabled}
      />
    );
  }

  if (dateDropdownOperations.includes(operation)) {
    return (
      <ProspectFiltersDateInput
        field={field}
        operation={operation}
        value={value as string}
        onUpdateFilterConditionValue={onUpdateFilterConditionValue}
        disabled={disabled}
      />
    );
  }

  return <span />;
};

export default ProspectFiltersValueComponent;
